import React from 'react';
import { observer, PropTypes as MobxPropTypes } from 'mobx-react';
import { DateTime } from 'luxon';
import PropTypes from 'prop-types';

import { OPTION_TYPES } from '../../helpers/ShippingOptionHelper';
import { AddressHelper } from '../../helpers/AddressHelper';
import { unIncludeDebitCartTax } from '../../helpers/CartHelper';
import { ProductLine } from './product-line';
import { enforceParameters } from '../../hocs/enforce-parameters';
import iconMap from './icon-map.svg';
import iconTruck from './icon-truck.svg';
import iconMoney from './icon-money.svg';
import iconDocument from './icon-document.svg';
import styles from './CheckoutSummary.css';
import bankData from '../../assets/images';
import { PaymentStore } from '../../pages/payment-methods-and-billing/stores/PaymentStore';
import { ConfigParametersStore } from '../../stores/ConfigParametersStore';

const ICartItem = PropTypes.shape({
  amount: PropTypes.number.isRequired,
  product: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }).isRequired,
});

const IPropTypes = {
  address: PropTypes.shape({
    additionalData: PropTypes.string,
    addressType: PropTypes.string.isRequired,
    cityName: PropTypes.shape({
      name: PropTypes.string.isRequired,
    }).isRequired,
    department: PropTypes.shape({
      name: PropTypes.string.isRequired,
    }).isRequired,
    doorNumber: PropTypes.string,
    firstCorner: PropTypes.string,
    streetName: PropTypes.string.isRequired,
  }),
  allowCoordinations: PropTypes.bool,
  billingData: PropTypes.shape({
    address: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    personalId: PropTypes.string,
    rut: PropTypes.string,
    department: PropTypes.string,
    locality: PropTypes.string,
  }),
  bank: PropTypes.shape({
    id: PropTypes.number,
    code: PropTypes.string,
    name: PropTypes.string,
  }),
  cardsClickable: PropTypes.bool,
  cart: MobxPropTypes.arrayOrObservableArrayOf(ICartItem).isRequired,
  checkoutId: PropTypes.string,
  isPickup: PropTypes.bool,
  minimalInfo: PropTypes.bool,
  onlyProductData: PropTypes.bool,
  shippingOption: PropTypes.shape({
    optionType: PropTypes.oneOf(Object.values(OPTION_TYPES)).isRequired,
    startHour: PropTypes.number,
    endHour: PropTypes.number,
    date: PropTypes.instanceOf(DateTime),
    futureDate: PropTypes.instanceOf(DateTime),
  }),
  installationData: MobxPropTypes.arrayOrObservableArrayOf(PropTypes.shape({
    productId: PropTypes.string.isRequired,
  })).isRequired,
  timeSlot: PropTypes.shape({
    date: PropTypes.instanceOf(DateTime),
    futureDate: PropTypes.instanceOf(DateTime),
    startHour: PropTypes.number,
    endHour: PropTypes.number,
  }),
  paymentMethodId: PropTypes.number,
};

const defaultProps = {
  address: null,
  allowCoordinations: false,
  billingData: null,
  cardsClickable: false,
  checkoutId: null,
  isPickup: false,
  minimalInfo: false,
  onlyProductData: false,
  shippingOption: null,
  timeSlot: null,
  paymentMethodId: 0,
  bank: null,
};

const formatDate = date => (
  `${date.toFormat('cccc d \'de\' MMMM')}`
);

const renderBillingInfoContent = billingData => (
  <div className={styles.infoContainer}>
    <div className={styles.infoIconContainer}>
      <img alt="" src={iconDocument} />
    </div>
    <div>
      <h4 className={styles.infoTitle}>
        {billingData.name}
      </h4>
      <p className={styles.infoLine}>
        {
          !!billingData.rut &&
          `RUT: ${billingData.rut}`
        }
        {
          !!billingData.personalId &&
          `Documento: ${billingData.personalId}`
        }
      </p>
      <p className={styles.infoLine}>
        {billingData.address}
      </p>
      <p className={styles.infoLine}>
        {`${billingData.locality}, ${billingData.department}`}
      </p>
    </div>
  </div>
);

const renderShippingInfoContent = (isPickup, shippingOption, timeSlot = null) => {
  if (!shippingOption && !isPickup) {
    return null;
  }
  if (isPickup) {
    return (
      <React.Fragment>
        <h4 className={styles.infoTitle}>
          Retiro en depósito (Montevideo)
        </h4>
      </React.Fragment>
    );
  }
  if (shippingOption.optionType === OPTION_TYPES.SIMPLE) {
    return (
      <React.Fragment>
        <h4 className={styles.infoTitle}>
          Envío común
        </h4>
        <p className={styles.infoLine}>
          A recibir el {shippingOption.futureDate ?
           formatDate(shippingOption.futureDate) : formatDate(shippingOption.date)} (
          {shippingOption.startHourString}hs a {shippingOption.endHourString}hs)
        </p>
      </React.Fragment>
    );
  } else if (shippingOption.optionType === OPTION_TYPES.SPECIAL) {
    return (
      <React.Fragment>
        <h4 className={styles.infoTitle}>
          Envío a hora personalizada
        </h4>
        <p className={styles.infoLine}>
          A recibir el {timeSlot.futureDate ?
            formatDate(timeSlot.futureDate) : formatDate(timeSlot.date)} (
          {timeSlot.startHourString}hs a {timeSlot.endHourString}hs)
        </p>
      </React.Fragment>
    );
  } else if (shippingOption.optionType === OPTION_TYPES.AGENCY) {
    return (
      <React.Fragment>
        <h4 className={styles.infoTitle}>
          Envío por agencia - {shippingOption.name}
        </h4>
        <p className={styles.infoLine}>
          A despachar en agencia el {timeSlot.futureDate ?
            formatDate(timeSlot.futureDate) : formatDate(timeSlot.date)} (
          {timeSlot.startHourString}hs a {timeSlot.endHourString}hs)
        </p>
      </React.Fragment>
    );
  }
  return null;
};

const renderAddressContent = address => (
  <div className={styles.infoContainer}>
    <div className={styles.infoIconContainer}>
      <img alt="" src={iconMap} />
    </div>
    <div>
      <h4 className={styles.infoTitle}>
        {address.streetName}
      </h4>
      <p className={styles.infoLine}>
        {AddressHelper.getAddressType(address.addressType)}
      </p>
      <p className={styles.infoLine}>
        {AddressHelper.getAddressSecondLine(address)}
      </p>
      <p className={styles.infoLine}>
        {address.cityName ? `${address.cityName.name}, ` : ''}{address.department.name}
      </p>
      {
        !!address.additionalData &&
        <p className={styles.infoLine}>
          {address.additionalData}
        </p>
      }
    </div>
  </div>
);

const renderProductLines = (
  checkoutId,
  cart,
  installationData,
  cardsAreClickable,
  allowCoordination,
  hasArticleInPresale,
) => (
  unIncludeDebitCartTax(cart).map((item) => {
    const productInstallation = installationData.find(data => data.productId === item.product.id);
    const selectedInstallations = !productInstallation ?
      0 :
      productInstallation.selectedOptions.reduce((acc, data) => acc + data.amount, 0);
    return (
      <React.Fragment key={item.product.id}>
        {
          item.serviceAmount > 0 &&
          !!productInstallation &&
          productInstallation.selectedOptions.map(option => (
            <ProductLine
              amount={option.amount}
              className={styles.productLine}
              checkoutId={checkoutId}
              clickable={cardsAreClickable}
              key={option.id}
              installationOption={option}
              item={item}
              hasService
            />
          ))
        }
        {
          item.serviceAmount > 0 &&
          selectedInstallations < item.serviceAmount &&
          <ProductLine
            amount={item.serviceAmount - selectedInstallations}
            allowCoordination={allowCoordination && !hasArticleInPresale}
            checkoutId={checkoutId}
            className={styles.productLine}
            clickable={cardsAreClickable}
            item={item}
            hasService
          />
        }
        {
          item.serviceAmount < item.amount &&
          <ProductLine
            amount={item.amount - item.serviceAmount}
            className={styles.productLine}
            checkoutId={checkoutId}
            clickable={cardsAreClickable}
            item={item}
          />
        }
      </React.Fragment>
    );
  })
);

const CheckoutSummary = (props) => {
  const {
    address,
    billingData,
    cart,
    checkoutId,
    isPickup,
    shippingOption,
    timeSlot,
    installationData,
    paymentMethodId,
    bank,
  } = props;
  const {
    paymentTypes,
  } = PaymentStore;
  const paymentMethod = paymentTypes && paymentTypes.find(pm => pm.id === paymentMethodId);
  const cartAmount = unIncludeDebitCartTax(cart).reduce((prev, cur) => prev + cur.amount, 0);
  const data = bank && bankData(String(bank.name).toLocaleLowerCase().trim().replace(/\s/g, ''));
  return (
    <div className={[styles.container, props.minimalInfo ? styles.noBorder : '']}>
      {
        !props.minimalInfo &&
        <React.Fragment>
          {
            !props.onlyProductData &&
            <h2 className={styles.title}>
              Resumen de pedido
            </h2>
          }
          <p className={styles.articleCount}>
            Articulos ({cartAmount})
          </p>
          <div>
            {renderProductLines(
              checkoutId,
              cart,
              installationData,
              props.cardsClickable,
              props.allowCoordinations,
              cart.map(item => item.product.id)
                .some(item => ConfigParametersStore.isValueInFeatureFlagData('product_next_deliveries', item)),
            )}
          </div>
        </React.Fragment>
      }
      {
        !props.onlyProductData &&
        <React.Fragment>
          {
            !!billingData &&
            renderBillingInfoContent(billingData)
          }
          {
            !!address &&
            renderAddressContent(address)
          }
          {
            (isPickup || !!shippingOption) &&
            <div className={styles.infoContainer}>
              <div className={styles.infoIconContainer}>
                <img alt="" src={iconTruck} />
              </div>
              <div>
                {renderShippingInfoContent(isPickup, shippingOption, timeSlot)}
              </div>
            </div>
          }
          {
            !!paymentMethod &&
            <div className={styles.infoContainer}>
              <div className={styles.infoIconContainer}>
                <img alt="" src={iconMoney} />
              </div>
              <div>
                <h4 className={styles.infoTitle}>{paymentMethod.description}</h4>
                <p className={styles.infoLine}>{paymentMethod.detail}</p>
                {
                  data &&
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <span>{data[0]}</span>
                      <img style={{ width: 60, marginLeft: 10 }} className={styles.fullPrice} alt="Bank" src={data[1]} />
                    </div>
                    {['SANTANDER'].includes(bank.name) && <div style={{ color: 'red', fontSize: 14, marginTop: 10 }}>Será redirigido al sitio del Santander para confirmar el pago. Luego, recibirá un mail con los detalles de su compra o podrá visualizarla dentro de su cuenta en nuestro sitio.</div>}
                  </div>
                }
              </div>
            </div>
          }
        </React.Fragment>
      }
    </div>
  );
};

CheckoutSummary.propTypes = IPropTypes;
CheckoutSummary.defaultProps = defaultProps;

const ObservedCheckoutSummary = enforceParameters(observer(CheckoutSummary));

export { ObservedCheckoutSummary as CheckoutSummary };



// WEBPACK FOOTER //
// ./src/common/checkout-summary/CheckoutSummary.jsx